// src/components/subscription/FeatureGates.jsx

import React, { useState } from 'react';
import { Lock, Sparkles, Crown, ArrowRight, X } from 'lucide-react';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useFeatureAccess, useUsageCheck } from '../../contexts/SubscriptionContext';
import { useRevenueCat } from '../../contexts/RevenueCatContext';
import toast from 'react-hot-toast';

// =====================================================
// UPGRADE MODAL COMPONENT
// =====================================================

export const UpgradeModal = ({ 
  isOpen, 
  onClose, 
  feature,
  title,
  description,
  limitInfo,
}) => {
  const { allTiers, billingEnabled, isBeta, monthlyPrice } = useSubscription();
  const { purchase, loading: rcLoading } = useRevenueCat();
  const [purchasing, setPurchasing] = useState(false);
  
  if (!isOpen || !billingEnabled) return null;
  
  const featureNames = {
    shows: 'Shows',
    inventoryItems: 'Inventory Items',
    documents: 'Documents',
    activePolls: 'Active Polls',
    bandMembers: 'Band Members',
    contacts: 'Contacts',
    expenses: 'Expenses',
    salesMode: 'Sales Mode',
    exportData: 'Data Export',
    bandsintownImport: 'Bandsintown Import',
    financialReports: 'Financial Reports',
    showMerchBreakdown: 'Merch Breakdown',
    prioritySupport: 'Priority Support',
  };

  const featureDescriptions = {
    shows: 'Track unlimited shows and never lose a gig',
    inventoryItems: 'Manage all your merch inventory',
    documents: 'Store unlimited contracts, riders, and files',
    activePolls: 'Run unlimited band polls at once',
    bandMembers: 'Add your whole crew to the band',
    contacts: 'Keep all your industry contacts organized',
    expenses: 'Track every expense on the road',
    salesMode: 'Track merch sales at shows in real-time',
    exportData: 'Export your data to spreadsheets',
    bandsintownImport: 'Import shows from Bandsintown automatically',
  };

  const handlePurchase = async () => {
    setPurchasing(true);
    
    try {
      const result = await purchase();
      
      if (result.success) {
        toast.success('Welcome to Pro! 🎉');
        onClose();
      } else if (result.cancelled) {
        // User cancelled - do nothing
      } else {
        toast.error('Purchase failed. Please try again.');
        console.error('Purchase error:', result.error);
      }
    } catch (err) {
      console.error('Purchase error:', err);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setPurchasing(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full shadow-2xl relative" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 text-center border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X size={20} />
          </button>
          
          <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Sparkles className="text-white" size={32} />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
            {title || `Unlock ${featureNames[feature] || feature}`}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {description || featureDescriptions[feature] || `Upgrade to Pro to access ${featureNames[feature] || 'this feature'}.`}
          </p>
          
          {/* Limit info */}
          {limitInfo && (
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded-full text-sm font-medium">
              <Lock size={14} />
              {limitInfo}
            </div>
          )}
        </div>
        
        {/* Pro Tier Benefits */}
        <div className="p-6 space-y-4">
          <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-2 border-purple-200 dark:border-purple-700 rounded-xl">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Crown className="text-purple-600 dark:text-purple-400" size={20} />
                <span className="font-bold text-gray-800 dark:text-gray-100">{allTiers?.pro?.name || 'Pro'}</span>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-gray-800 dark:text-gray-100">{monthlyPrice}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">/month</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {allTiers?.pro?.description || 'Unlimited everything for serious bands'}
            </p>
          </div>
          
          <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-2 px-2">
            <li className="flex items-center gap-2">
              <span className="text-green-500">✓</span> Unlimited shows, members & inventory
            </li>
            <li className="flex items-center gap-2">
              <span className="text-green-500">✓</span> Sales Mode for merch tracking
            </li>
            <li className="flex items-center gap-2">
              <span className="text-green-500">✓</span> Export data to CSV
            </li>
            <li className="flex items-center gap-2">
              <span className="text-green-500">✓</span> Bandsintown import
            </li>
            <li className="flex items-center gap-2">
              <span className="text-green-500">✓</span> Unlimited contacts & expenses
            </li>
          </ul>
        </div>
        
        {/* Beta notice */}
        {isBeta && (
          <div className="px-6 pb-4">
            <p className="text-xs text-center text-gray-500 dark:text-gray-400 bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 rounded-lg py-2">
              🎉 Beta users get 50% off when paid plans launch!
            </p>
          </div>
        )}
        
        {/* Footer */}
        <div className="p-6 pt-0 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 text-gray-600 dark:text-gray-400 font-medium rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            Maybe Later
          </button>
          <button
            onClick={handlePurchase}
            disabled={purchasing || rcLoading}
            className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-pink-700 transition-colors flex items-center justify-center gap-2 shadow-lg shadow-purple-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {purchasing ? (
              <>
                <span className="animate-spin">⏳</span>
                Processing...
              </>
            ) : (
              <>
                Upgrade Now
                <ArrowRight size={18} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// =====================================================
// GATED WRAPPER COMPONENT
// =====================================================
// Wraps children with lock styling and click-to-upgrade
// Shows dimmed button with PRO badge when locked
// =====================================================

const GatedWrapper = ({ 
  children, 
  isLocked, 
  feature, 
  limitInfo,
  variant = 'limit', // 'limit' | 'pro-only'
}) => {
  const [showModal, setShowModal] = useState(false);
  
  if (!isLocked) {
    return <>{children}</>;
  }
  
  return (
    <>
      <div 
        className="gated-feature"
        data-variant={variant}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setShowModal(true);
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setShowModal(true);
          }
        }}
        style={{
          position: 'relative',
          display: 'inline-flex',
          cursor: 'pointer',
        }}
      >
        <div 
          className="gated-feature__content"
          style={{
            opacity: 0.5,
            filter: 'grayscale(50%)',
            pointerEvents: 'none',
          }}
        >
          {children}
        </div>
        <div 
          className="gated-feature__badge"
          style={{
            position: 'absolute',
            top: '-6px',
            right: '-6px',
            display: 'flex',
            alignItems: 'center',
            gap: '2px',
            padding: '2px 6px',
            fontSize: '9px',
            fontWeight: '700',
            textTransform: 'uppercase',
            color: 'white',
            background: 'linear-gradient(to right, #9333ea, #ec4899)',
            borderRadius: '9999px',
            boxShadow: '0 2px 4px rgba(147, 51, 234, 0.3)',
            zIndex: 10,
          }}
        >
          <Crown size={10} />
          <span>PRO</span>
        </div>
      </div>
      
      <UpgradeModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        feature={feature}
        limitInfo={limitInfo}
      />
    </>
  );
};

// =====================================================
// USAGE LIMIT GATE (Base Component)
// =====================================================

export const UsageGate = ({ 
  feature, 
  currentUsage, 
  children, 
  showWhenLocked = true,
}) => {
  const { allowed, remaining, limit, atLimit } = useUsageCheck(feature, currentUsage);
  const { billingEnabled } = useSubscription();
  
  // If billing is disabled, always allow
  if (!billingEnabled) {
    return <>{children}</>;
  }
  
  // Within limits - show normally
  if (allowed && !atLimit) {
    return <>{children}</>;
  }
  
  // At limit - show locked version or hide
  if (!showWhenLocked) {
    return null;
  }
  
  return (
    <GatedWrapper 
      isLocked={true} 
      feature={feature}
      limitInfo={`${currentUsage} of ${limit} used`}
      variant="limit"
    >
      {children}
    </GatedWrapper>
  );
};

// =====================================================
// PAID FEATURE GATE (Base Component)
// =====================================================

export const PaidFeature = ({ 
  feature, 
  children, 
  showWhenLocked = true,
}) => {
  const hasAccess = useFeatureAccess(feature);
  const { billingEnabled } = useSubscription();
  
  // If billing is disabled, always show
  if (!billingEnabled || hasAccess) {
    return <>{children}</>;
  }
  
  // No access - show locked version or hide
  if (!showWhenLocked) {
    return null;
  }
  
  return (
    <GatedWrapper 
      isLocked={true} 
      feature={feature}
      variant="pro-only"
    >
      {children}
    </GatedWrapper>
  );
};

// =====================================================
// UPGRADE BADGE (Inline)
// =====================================================

export const UpgradeBadge = ({ feature, size = 'sm' }) => {
  const hasAccess = useFeatureAccess(feature);
  const { billingEnabled } = useSubscription();
  
  if (!billingEnabled || hasAccess) {
    return null;
  }
  
  const sizeClasses = {
    xs: 'text-[9px] px-1 py-0.5',
    sm: 'text-[10px] px-1.5 py-0.5',
    md: 'text-xs px-2 py-1',
  };
  
  return (
    <span className={`inline-flex items-center gap-0.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold rounded-full uppercase ${sizeClasses[size]}`}>
      <Crown size={size === 'xs' ? 8 : size === 'sm' ? 10 : 12} />
      PRO
    </span>
  );
};

// =====================================================
// USAGE LIMIT BANNER
// =====================================================

export const UsageLimitBanner = ({ feature, currentUsage }) => {
  const [dismissed, setDismissed] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { allowed, remaining, limit, atLimit } = useUsageCheck(feature, currentUsage);
  const { billingEnabled } = useSubscription();
  
  if (!billingEnabled || dismissed) return null;
  if (limit === Infinity) return null;
  
  const percentage = (currentUsage / limit) * 100;
  
  // Don't show if under 70%
  if (percentage < 70) return null;
  
  const featureNames = {
    shows: 'shows',
    inventoryItems: 'inventory items',
    documents: 'documents',
    activePolls: 'active polls',
    bandMembers: 'band members',
    contacts: 'contacts',
    expenses: 'expenses',
  };
  
  const name = featureNames[feature] || feature;
  
  if (atLimit) {
    return (
      <>
        <div className="bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 mb-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-red-100 dark:bg-red-900/40 rounded-lg">
              <Lock size={20} className="text-red-600 dark:text-red-400" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-red-800 dark:text-red-200">
                You've reached your {name} limit
              </p>
              <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                Upgrade to Pro for unlimited {name}.
              </p>
            </div>
            <button 
              onClick={() => setShowModal(true)}
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-sm font-medium rounded-lg transition-colors shadow-lg shadow-purple-500/25"
            >
              Upgrade
            </button>
          </div>
        </div>
        <UpgradeModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          feature={feature}
          limitInfo={`${currentUsage} of ${limit} used`}
        />
      </>
    );
  }
  
  // Warning state (70-99%)
  return (
    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 mb-4">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <p className="text-sm text-amber-800 dark:text-amber-200">
            <span className="font-semibold">{remaining} {name}</span> remaining on your free plan.
            {' '}
            <button 
              onClick={() => setShowModal(true)}
              className="text-purple-600 dark:text-purple-400 font-medium hover:underline"
            >
              Upgrade for unlimited
            </button>
          </p>
        </div>
        <button 
          onClick={() => setDismissed(true)}
          className="text-amber-400 hover:text-amber-600 dark:hover:text-amber-300 text-xl leading-none"
        >
          ×
        </button>
      </div>
    </div>
  );
};


// =====================================================
// SPECIFIC FEATURE GATE WRAPPERS
// =====================================================
// Convenience wrappers for each gated feature
// These provide a cleaner API for the most common use cases
// =====================================================

// --- USAGE-LIMITED GATES (count-based limits) ---

export const AddShowGate = ({ currentCount, children }) => (
  <UsageGate feature="shows" currentUsage={currentCount}>
    {children}
  </UsageGate>
);

export const AddMemberGate = ({ currentCount, children }) => (
  <UsageGate feature="bandMembers" currentUsage={currentCount}>
    {children}
  </UsageGate>
);

export const AddInventoryGate = ({ currentCount, children }) => (
  <UsageGate feature="inventoryItems" currentUsage={currentCount}>
    {children}
  </UsageGate>
);

export const AddDocumentGate = ({ currentCount, children }) => (
  <UsageGate feature="documents" currentUsage={currentCount}>
    {children}
  </UsageGate>
);

export const AddContactGate = ({ currentCount, children }) => (
  <UsageGate feature="contacts" currentUsage={currentCount}>
    {children}
  </UsageGate>
);

export const AddExpenseGate = ({ currentCount, children }) => (
  <UsageGate feature="expenses" currentUsage={currentCount}>
    {children}
  </UsageGate>
);

export const AddPollGate = ({ currentCount, children }) => (
  <UsageGate feature="activePolls" currentUsage={currentCount}>
    {children}
  </UsageGate>
);

// --- PRO-ONLY FEATURE GATES (boolean access) ---

export const SalesModeGate = ({ children }) => (
  <PaidFeature feature="salesMode">
    {children}
  </PaidFeature>
);

export const ExportGate = ({ children }) => (
  <PaidFeature feature="exportData">
    {children}
  </PaidFeature>
);

export const BandsintownImportGate = ({ children }) => (
  <PaidFeature feature="bandsintownImport">
    {children}
  </PaidFeature>
);